/*
 * Per-product gift wrapping.
 *
 * <gift-wrap-controller> keeps a single collected gift-wrap line in the cart
 * whose quantity equals the total quantity of all line items flagged with the
 * "Cadeauverpakking: Ja" property. It reacts to the theme's existing cart events
 * (afterCartChanged, afterUpdateQuantity) so adding a wrapped product, changing
 * a quantity, or toggling a line all keep the wrap line in sync.
 *
 * <gift-wrap-line-toggle> is the per-line checkbox shown in the cart. It stops
 * its change event from bubbling to <cart-items> (which would mis-read it as a
 * quantity change) and asks the controller to flip the line's property.
 */
if (!customElements.get('gift-wrap-controller')) {
  customElements.define('gift-wrap-controller', class GiftWrapController extends HTMLElement {
    connectedCallback() {
      this.variantId = parseInt(this.dataset.variantId, 10);
      this.propName = this.dataset.propName || 'Cadeauverpakking';
      this.propValue = this.dataset.propValue || 'Ja';
      this.cart =
        document.querySelector('cart-drawer') ||
        document.querySelector('cart-notification') ||
        document.querySelector('cart-items');
      this.busy = false;
      this.pending = false;

      this.onCartEvent = this.onCartEvent.bind(this);
      document.addEventListener('afterCartChanged', this.onCartEvent);
      document.addEventListener('afterUpdateQuantity', this.onCartEvent);
    }

    disconnectedCallback() {
      document.removeEventListener('afterCartChanged', this.onCartEvent);
      document.removeEventListener('afterUpdateQuantity', this.onCartEvent);
    }

    onCartEvent(e) {
      // Ignore events we triggered ourselves to avoid loops.
      if (e.detail && e.detail.source === 'gift-wrap') return;
      this.sync();
    }

    isWrapLine(item) {
      return item.variant_id === this.variantId || (item.properties && item.properties._giftwrap);
    }

    isWrapped(item) {
      return item.properties && item.properties[this.propName] === this.propValue;
    }

    sectionIds() {
      if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
        return this.cart.getSectionsToRender().map((s) => s.id);
      }
      return [];
    }

    async getCart() {
      const res = await fetch(`${routes.cart_url}.js`);
      return res.json();
    }

    render(parsed) {
      if (this.cart && parsed.sections && typeof this.cart.renderContents === 'function') {
        this.cart.renderContents(parsed, null, null);
      }
      document.dispatchEvent(new CustomEvent('afterUpdateQuantity', {
        detail: { source: 'gift-wrap', cartData: parsed }
      }));
    }

    // Recompute the desired wrap quantity from cart state and apply it.
    async sync(state) {
      if (!this.variantId) return;
      if (this.busy) { this.pending = true; return; }

      const cart = state || await this.getCart();
      let desired = 0;
      let wrapLine = null;
      cart.items.forEach((item) => {
        if (this.isWrapLine(item)) { wrapLine = item; return; }
        if (this.isWrapped(item)) desired += item.quantity;
      });
      const current = wrapLine ? wrapLine.quantity : 0;
      if (desired === current) return;

      this.busy = true;
      try {
        let url, body;
        const sections = this.sectionIds();
        if (wrapLine) {
          url = routes.cart_change_url;
          body = JSON.stringify({
            id: wrapLine.key,
            quantity: desired,
            sections: sections,
            sections_url: window.location.pathname
          });
        } else {
          url = routes.cart_add_url;
          body = JSON.stringify({
            items: [{ id: this.variantId, quantity: desired, properties: { _giftwrap: 'true' } }],
            sections: sections,
            sections_url: window.location.pathname
          });
        }
        const res = await fetch(url, { ...fetchConfig(), body });
        const parsed = await res.json();
        this.render(parsed);
      } finally {
        this.busy = false;
        if (this.pending) { this.pending = false; this.sync(); }
      }
    }

    // Flip the "Cadeauverpakking" property on a single line, then re-sync.
    // change.js with `properties` is unreliable (the line key is derived from
    // the properties), so we remove the line and re-add it with the new set.
    async toggleLine(key, checked) {
      if (this.busy) { this.pending = true; return; }
      this.busy = true;
      try {
        const cart = await this.getCart();
        const item = cart.items.find((i) => i.key === key);
        if (!item) return;

        const props = Object.assign({}, item.properties);
        if (checked) props[this.propName] = this.propValue;
        else delete props[this.propName];

        // Remove the old line.
        await fetch(routes.cart_change_url, {
          ...fetchConfig(),
          body: JSON.stringify({ id: key, quantity: 0 })
        });

        // Re-add it with the updated properties.
        const addBody = {
          id: item.variant_id,
          quantity: item.quantity,
          properties: props,
          sections: this.sectionIds(),
          sections_url: window.location.pathname
        };
        if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) {
          addBody.selling_plan = item.selling_plan_allocation.selling_plan.id;
        }
        const res = await fetch(routes.cart_add_url, {
          ...fetchConfig(),
          body: JSON.stringify(addBody)
        });
        const parsed = await res.json();
        this.render(parsed);
      } finally {
        this.busy = false;
      }
      // Re-sync the collected wrap line against the new cart state.
      await this.sync();
    }
  });
}

if (!customElements.get('gift-wrap-line-toggle')) {
  customElements.define('gift-wrap-line-toggle', class GiftWrapLineToggle extends HTMLElement {
    connectedCallback() {
      this.checkbox = this.querySelector('input[type="checkbox"]');
      this.key = this.dataset.lineKey;
      this.onChange = this.onChange.bind(this);
      if (this.checkbox) this.checkbox.addEventListener('change', this.onChange);
    }

    disconnectedCallback() {
      if (this.checkbox) this.checkbox.removeEventListener('change', this.onChange);
    }

    onChange(e) {
      // Prevent <cart-items> from treating this as a quantity change.
      e.stopPropagation();
      const controller = document.querySelector('gift-wrap-controller');
      if (controller) controller.toggleLine(this.key, this.checkbox.checked);
    }
  });
}
